import { useAuroraStore } from '../../store/auroraStore'
import { authorName, members, toPlanBlock } from '../../store/selectors'
import { BlockCard } from '../today/BlockCard'
import { ToolActivity } from './ToolActivity'
import { ArtifactChip } from './ArtifactChip'
import { Markdown } from './Markdown'
import { splitAtCards, toolDone } from '@aurora/core'

/**
 * One turn.
 *
 * The parent's words sit in a bubble; the agent's do not — assistant replies are
 * often long and thoughtful, and prose in the content column reads far better
 * than a giant tinted blob.
 */
export function Message({ message, isLast }: { message: any; isLast: boolean }) {
  const attachments: any[] = message.attachments ?? []
  const artifactIds = attachments
    .filter((a) => a?.kind === 'artifact' && a.artifactId)
    .map((a) => a.artifactId)
  // A name on every bubble once the thread has more than one person in it;
  // alone, a parent knows their own words.
  const author = useAuroraStore((s: any) => (members(s).length > 1 ? authorName(s, message.authorId) : undefined))

  if (message.role === 'user') {
    return (
      <div className="msg msg-user">
        {author && <span className="msg-author">{author}</span>}
        <div className="msg-user-bubble">
          {message.content}
          {artifactIds.length > 0 && (
            <div className="msg-attachments">
              {artifactIds.map((id: string) => <ArtifactChip key={id} artifactId={id} />)}
            </div>
          )}
        </div>
      </div>
    )
  }

  // A step that made something the reply shows as a card ("Asked for version
  // 2" above version 2's own card) says what the card already says.
  const trail = (message.toolCalls ?? []).filter((c: any) => !(toolDone(c) && c.output?.status !== 'waiting' && madeId(c) && artifactIds.includes(madeId(c)!)))

  return (
    <div className="msg msg-agent">
      {trail.length > 0 && <ToolActivity calls={trail} />}

      {splitAtCards(message.content, attachments).map((p, i) =>
        'text' in p ? <Markdown key={i} text={p.text} /> : <ShownCards key={i} cards={p.cards} queued={queuedChanges(message.toolCalls)} />,
      )}

      {message.streaming && (
        <span className="msg-cursor" aria-label="Sapling is writing" />
      )}

      {message.error && (
        <p className="msg-error">
          {message.error}
        </p>
      )}
    </div>
  )
}

/** The game a tool step made or changed. Synced rows reach the store camelCased all the way down. */
const madeId = (c: any): string | undefined => c.output?.artifactId

/**
 * The changes this reply asked for that did not start a version but wait on
 * the one being made, by the game they wait on, with the name Sapling gave
 * each. Their card is the change waiting, not the version already underway.
 */
function queuedChanges(calls: any[] | undefined): Map<string, string | undefined> {
  return new Map(
    (calls ?? [])
      .filter((c) => c.name === 'make_game' && c.output?.status === 'waiting' && madeId(c))
      .map((c) => [madeId(c)!, typeof c.input?.label === 'string' ? c.input.label : undefined]),
  )
}

/** Cards the agent showed: activities as the plan's own card, runnable here; things it made as their card. */
function ShownCards({ cards, queued }: { cards: any[]; queued: Map<string, string | undefined> }) {
  const blocks = useAuroraStore((s: any) =>
    cards.filter((a) => a.kind === 'block').map((a) => s.blocks[a.blockId]).filter((b: any) => b && !b.deleted),
  )
  const made = cards.filter((a) => a.kind === 'artifact').map((a) => a.artifactId)
  return (
    <div className="msg-blocks">
      {blocks.map((b: any) => <BlockCard key={b._id} block={toPlanBlock(b)} date={b.date} />)}
      {made.map((id: string) => <ArtifactChip key={id} artifactId={id} expanded queued={queued.has(id) ? { label: queued.get(id) } : undefined} />)}
    </div>
  )
}
